import React from 'react';
import { renderInlineMarkup } from '@source-components-react/shared/inline-markup';
import { DEFAULT_CONTENT_TEXT } from './default-attribute-values';
import { resolveEnclosingTag } from './resolve-enclosing-tag';
import type { EnclosingTag } from './enclosing-tags';

export function RText({
  enclosingTag,
  componentAttributes,
  styleProps,
  className,
}: Readonly<{
  /**
   * HTML tag to enclose the text in (e.g. 'p', 'h1', 'span'). Restricted to
   * tags that hold text without changing its behavior. When omitted the text
   * renders bare, exactly as before this attribute existed.
   */
  enclosingTag?: EnclosingTag;
  componentAttributes?: { [key: string]: any };
  /**
   * Column styles from the widget pipeline. Applied to the enclosing tag when
   * there is one; bare text has no element to carry them, in which case
   * `resolveNoContainer` has already told widget-react to use a wrapper div.
   */
  styleProps?: { [key: string]: any };
  /**
   * Host-app classes for this column, when the renderer decided this card
   * carries them. Applied to the enclosing tag; ignored for bare text (the
   * renderer has already taken responsibility for the class).
   */
  className?: string;
}>) {
  const contentVal =
    componentAttributes?.['content-text-3-resolutions'] ?? DEFAULT_CONTENT_TEXT;

  // Marks (`{b|word}`), line breaks (`{br|}`), and the two index markers —
  // `{n|…}` into the link list and `{.n|…}` into the styling list — both by
  // 1-based position. Everything the markup emits is phrasing content inside
  // the enclosing tag, never around it.
  const content =
    typeof contentVal === 'string'
      ? renderInlineMarkup(
          contentVal,
          componentAttributes?.['links-3-resolutions'],
          componentAttributes?.['inline-styles-3-resolutions']
        )
      : contentVal;

  const tag = resolveEnclosingTag(componentAttributes, enclosingTag);

  if (tag) {
    return React.createElement(
      tag,
      className !== undefined ? { style: styleProps, className } : { style: styleProps },
      content
    );
  }
  return content;
}
